// TabNavigation.js
import React, { useRef } from 'react';
import { useAssessment } from './SharedContext';
import {
    FileText,
    GraduationCap,
    Users,
    Upload,
    Sparkles,
    BookOpen,
    HelpCircle,
    Settings,
    Image,
    Download
} from 'lucide-react';
import { useGradingPolicies } from '../hooks/useGradingPolicies';
import { buildExportDisplayName, formatVersion } from '../utils/sessionExport';

const ASSIGNMENT_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

const TabNavigation = () => {
    const {
        activeTab,
        setActiveTab,
        sharedRubric,
        persistentFormData,
        classList,
        gradingSession,
        exportSession,
        importSession,
        sessionExpirationEnabled,
        setSessionExpirationEnabled,
        sessionMetadata,
        setSessionMetadata,
    } = useAssessment();

    const { data: availablePolicies = [], isLoading: policiesLoading } =
        useGradingPolicies({ isActive: true });

    // For the Import button
    const importSessionInputRef = useRef(null);
    const handleSessionImport = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => importSession(evt.target.result);
        reader.readAsText(file);
        e.target.value = '';
    };

    // Session metadata field handlers
    const handleLetterChange = (e) => {
        setSessionMetadata(prev => ({ ...prev, assignmentLetter: e.target.value }));
    };
    const handleNumberChange = (e) => {
        const val = parseInt(e.target.value, 10);
        setSessionMetadata(prev => ({ ...prev, assignmentNumber: isNaN(val) ? 1 : Math.max(1, val) }));
    };
    const handleSessionNameChange = (e) => {
        setSessionMetadata(prev => ({ ...prev, sessionName: e.target.value }));
    };
    const handleVersionChange = (e) => {
        // Allow user to type, but sanitize on blur
        setSessionMetadata(prev => ({ ...prev, version: e.target.value }));
    };
    const handleVersionBlur = () => {
        setSessionMetadata(prev => ({ ...prev, version: formatVersion(prev.version) }));
    };

    const displayName = buildExportDisplayName(sessionMetadata);

    // === tabs definition ===
    const tabs = [
        { id: 'assignment-prompt-generator', name: 'Assignment Details', icon: BookOpen, description: 'Define assignment details, criteria, and generate HTML pages', activeClass: 'active-orange' },
        { id: 'ai-prompt-generator', name: 'AI Rubric Prompt', icon: Sparkles, description: 'Generate AI prompts to create rubric JSON files', activeClass: 'active-blue' },
        { id: 'rubric-creator', name: 'Rubric Builder', icon: FileText, description: 'Create and edit assessment rubrics', activeClass: 'active-purple' },
        { id: 'class-manager', name: 'Class Manager', icon: Users, description: 'Import and manage class lists', activeClass: 'active-indigo' },
        { id: 'grading-tool', name: 'Grading Tool', icon: GraduationCap, description: 'Grade assignments using rubrics', activeClass: 'active-green' },
        { id: 'gradebook', name: 'Grade Book', icon: BookOpen, description: 'Comprehensive gradebook with multiple assignments', activeClass: 'active-blue' },
        { id: 'policy-manager', name: 'Policy Manager', icon: Settings, description: 'Manage dynamic grading policies and scales', activeClass: 'active-teal' },
        { id: 'logo-settings', name: 'Logo Settings', icon: Image, description: 'Upload and manage school logo for exports', activeClass: 'active-pink' },
        { id: 'help', name: 'Help', icon: HelpCircle, description: 'User guide and feature documentation', activeClass: 'active-gray' },
    ];

    // some booleans for the little badges…
    const hasRubricData = !!sharedRubric?.assignmentInfo?.title;
    const hasFormData = !!persistentFormData?.student?.name
        || !!persistentFormData?.course?.name
        || !!persistentFormData?.assignment?.name
        || (persistentFormData.attachments?.length > 0)
        || (persistentFormData.videoLinks?.length > 0);
    const hasClassListData = classList?.students?.length > 0;
    const hasActiveSession = gradingSession?.active;
    const hasPolicyData = availablePolicies.length > 0;
    const currentProgramType = classList?.courseMetadata?.programType || 'degree';

    // progress %
    const getGradingProgress = () => {
        if (!classList?.gradingProgress) return { completed: 0, total: 0, percentage: 0 };
        const completed = classList.gradingProgress.filter(p => p.status?.startsWith('completed')).length;
        const total = classList.students.length;
        return { completed, total, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
    };
    const progress = getGradingProgress();
    const gradebookStats = { students: classList?.students?.length || 0, projects: 0 };

    // logo from sessionStorage
    const getLogoStatus = () => {
        try {
            const logo = sessionStorage.getItem('schoolLogo');
            const name = sessionStorage.getItem('schoolLogoName');
            return { hasLogo: !!logo, logoName: name || 'No logo' };
        } catch {
            return { hasLogo: false, logoName: 'No logo' };
        }
    };
    const logoStatus = getLogoStatus();

    // Helper to render inline status pills for each tab
    const renderStatusPills = (tabId) => {
        switch (tabId) {
            case 'ai-prompt-generator':
                return hasRubricData ? (
                    <span className="status-compact">
                        <span className="status-pill blue">✓ AI Ready</span>
                    </span>
                ) : null;

            case 'rubric-creator':
                return hasRubricData ? (
                    <span className="status-compact">
                        <span className="status-pill purple">✓ Ready</span>
                    </span>
                ) : null;

            case 'class-manager':
                return (
                    <span className="status-compact">
                        {hasClassListData && (
                            <span className="status-pill indigo">{classList.students.length}</span>
                        )}
                        {progress.total > 0 && (
                            <span className={`status-pill ${progress.percentage === 100 ? 'green' : 'red'}`}>
                                {progress.percentage}% ✓
                            </span>
                        )}
                        {hasActiveSession && (
                            <span className="status-pill orange">Active</span>
                        )}
                    </span>
                );

            case 'policy-manager':
                return (
                    <span className="status-compact">
                        {hasPolicyData && (
                            <span className="status-pill teal">{availablePolicies.length}</span>
                        )}
                        <span className="status-pill blue">
                            {currentProgramType.charAt(0).toUpperCase() + currentProgramType.slice(1)}
                        </span>
                        {policiesLoading && (
                            <span className="status-pill yellow">Loading...</span>
                        )}
                    </span>
                );

            case 'grading-tool':
                return (
                    <span className="status-compact">
                        {hasRubricData && (
                            <span className="status-pill green">✓ Ready</span>
                        )}
                        {hasFormData && (
                            <span className="status-pill blue">Data ✓</span>
                        )}
                        {hasActiveSession && (
                            <span className="status-pill orange">In Progress</span>
                        )}
                    </span>
                );

            case 'gradebook':
                return hasClassListData ? (
                    <span className="status-compact">
                        <span className="status-pill blue">{gradebookStats.students}</span>
                        {gradebookStats.projects > 0 && (
                            <span className="status-pill green">{gradebookStats.projects} Projects</span>
                        )}
                    </span>
                ) : null;

            case 'logo-settings':
                return (
                    <span className="status-compact">
                        <span className={`status-pill ${logoStatus.hasLogo ? 'green' : 'gray'}`}>
                            {logoStatus.hasLogo ? '✓ Ready' : 'No Logo'}
                        </span>
                    </span>
                );

            case 'help':
                return (
                    <span className="status-compact">
                        <span className="status-pill gray">Guide</span>
                    </span>
                );

            default:
                return null;
        }
    };

    return (
        <div className="tab-navigation border-b border-gray-200 shadow-sm">
            {/* ——— Compact Primary Tabs Bar ——— */}
            <nav
                className="compact-nav-bar"
                aria-label="Tabs"
            >
                {tabs.map(tab => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;

                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            aria-current={isActive ? 'page' : undefined}
                            title={tab.description}
                            className={`compact-nav-item ${isActive ? tab.activeClass : ''}`}
                        >
                            <span className="nav-row">
                                <Icon size={16} className="nav-icon" />
                                <span className="nav-text">{tab.name}</span>
                            </span>
                            {renderStatusPills(tab.id)}
                        </button>
                    );
                })}
            </nav>

            {/* ——— Compact Quick Actions Bar ——— */}
            <div className="compact-toolbar">
                <span className="toolbar-section-title">
                    Grading Session
                </span>

                {/* Session Naming Fields */}
                <div className="session-metadata-fields">
                    <select
                        value={sessionMetadata.assignmentLetter}
                        onChange={handleLetterChange}
                        className="toolbar-select"
                        aria-label="Assignment Letter"
                        title="Assignment Letter"
                    >
                        {ASSIGNMENT_LETTERS.map(letter => (
                            <option key={letter} value={letter}>{letter}</option>
                        ))}
                    </select>

                    <input
                        type="number"
                        value={sessionMetadata.assignmentNumber}
                        onChange={handleNumberChange}
                        min="1"
                        className="toolbar-input-number"
                        aria-label="Assignment Number"
                        title="Assignment Number"
                    />

                    <input
                        type="text"
                        value={sessionMetadata.sessionName}
                        onChange={handleSessionNameChange}
                        placeholder="Session Name (e.g. Biped Jump)"
                        className="toolbar-input toolbar-input-name"
                        aria-label="Session Name"
                    />

                    <div className="version-field">
                        <span className="version-prefix">v</span>
                        <input
                            type="text"
                            value={sessionMetadata.version}
                            onChange={handleVersionChange}
                            onBlur={handleVersionBlur}
                            maxLength={3}
                            className="toolbar-input-version"
                            aria-label="Version"
                            title="Version (3-digit)"
                        />
                    </div>
                </div>

                {/* Display name preview */}
                <span className="session-display-name" title={displayName}>
                    {displayName}
                </span>

                <div className="action-buttons">
                    <input
                        ref={importSessionInputRef}
                        type="file"
                        accept=".json"
                        onChange={handleSessionImport}
                        className="hidden"
                    />
                    <button
                        onClick={() => importSessionInputRef.current?.click()}
                        className="btn-import"
                        title="Import Session"
                    >
                        <Upload size={15} className="btn-icon" /> Import
                    </button>
                    <button
                        onClick={() => exportSession()}
                        className="btn-export"
                        title="Export Current Session"
                    >
                        <Download size={15} className="btn-icon" /> Export
                    </button>
                </div>

                {activeTab !== 'help' && (
                    <button
                        onClick={() => setActiveTab('help')}
                        className="help-link"
                        title="Need help? View user guide"
                    >
                        <HelpCircle size={14} /> Need Help?
                    </button>
                )}

                {activeTab !== 'policy-manager' && hasClassListData && (
                    <button
                        onClick={() => setActiveTab('policy-manager')}
                        className="btn-secondary"
                        title="Manage grading policies for your program type"
                    >
                        <Settings size={14} /> Manage Policies
                    </button>
                )}

                <div className="toggle-section">
                    <span>Session Expiration</span>
                    <button
                        onClick={() => setSessionExpirationEnabled(!sessionExpirationEnabled)}
                        className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 ${sessionExpirationEnabled ? 'bg-blue-600' : 'bg-gray-300'}`}
                        role="switch"
                        aria-checked={sessionExpirationEnabled}
                        aria-label="Toggle one-hour session expiration"
                        title="Toggle automatic one-hour session expiration"
                    >
                        <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${sessionExpirationEnabled ? 'translate-x-5' : 'translate-x-1'}`}
                        />
                    </button>
                    <span className={`toggle-label ${sessionExpirationEnabled ? 'on' : 'off'}`}>
                        {sessionExpirationEnabled ? 'On (1 hr)' : 'Off'}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default TabNavigation;
